import { Injectable } from '@angular/core';
import { CustomerDirectoryService } from './customer-directory.service';

/**
 * Per-customer brand colors, lazily hydrated from the customer-directory
 * list endpoint. Drives:
 *
 *   - Row-tint on every row in admin grids (Error Reports, Commerce
 *     Events, …) so operators can pattern-match on color instead of
 *     reading the slug. Mirrors the Users page convention: 6%/3%
 *     alternating opacity zebra, slate-400 fallback for the legacy
 *     'f2' slug.
 *
 *   - The 2-second live-row-fresh SSE pulse via the `--row-flash-color`
 *     CSS variable.
 *
 *   - The "new since you last looked" arrival highlight (18% opacity
 *     so it visibly dominates the zebra tint underneath).
 *
 * Source field is `brand_color` on the Customer doc — same field the
 * Users grid uses (see users.component._row_tint_style). The newer
 * `branding.accent_color` is consulted as a fallback when brand_color
 * isn't set, so a customer with only the branding sub-doc still tints.
 */

interface CustomerColors {
  brand_color?: string;
}

@Injectable({ providedIn: 'root' })
export class CustomerColorsService {

  private _colors: Record<string, CustomerColors> = {};
  private _hydrated = false;
  private _inflight: Promise<void> | null = null;

  constructor(private customerDir: CustomerDirectoryService) {}

  async hydrate(): Promise<void> {
    if (this._hydrated) return;
    if (this._inflight) return this._inflight;
    this._inflight = (async () => {
      try {
        const list = await this.customerDir.list();
        const next: Record<string, CustomerColors> = {};
        for (const c of (list || [])) {
          if (!c?._id) continue;
          // Prefer brand_color (Users page convention); fall back to
          // branding.accent_color (newer dynamic-CSS field).
          const top = String((c as any)?.brand_color || '').trim();
          const accent = String((c as any)?.branding?.accent_color || '').trim();
          const picked = top || accent;
          if (picked) next[c._id] = { brand_color: picked };
        }
        this._colors = next;
        this._hydrated = true;
      } finally {
        this._inflight = null;
      }
    })();
    return this._inflight;
  }

  /**
   * Brand color hex for the slug, or a slate-400 fallback for the
   * legacy 'f2' slug (matches the Users page behaviour so admin-side
   * rows from before the slug split still get a neutral tint instead
   * of staying transparent). Returns null when neither applies.
   */
  brandColorFor(slug: string | null | undefined): string | null {
    if (!slug) return null;
    const c = this._colors[slug];
    let hex = c?.brand_color || (slug === 'f2' ? '#94a3b8' : '');
    if (!hex) return null;
    if (!/^#[0-9a-fA-F]{3,8}$/.test(hex)) return null;
    return hex;
  }

  // Kept for the existing flashRgb / newRowTint callers — same semantics
  // as brandColorFor under the renamed source field.
  accentFor(slug: string | null | undefined): string | null {
    return this.brandColorFor(slug);
  }

  /**
   * Alternating-opacity row tint string for ag-grid getRowStyle —
   * mirrors the Users page _row_tint_style: 6%/3% (hex AA = 10/08)
   * alternating by rowIndex. Returns '' (caller treats as 'no tint')
   * when the slug has no color and isn't the legacy 'f2' fallback.
   */
  rowZebraTint(slug: string | null | undefined, rowIndex: number): string {
    const color = this.brandColorFor(slug);
    if (!color) return '';
    const opacityHex = rowIndex % 2 === 0 ? '10' : '08';
    return `${color}${opacityHex}`;
  }

  /**
   * Returns a faint background tint derived from the customer's accent
   * color — appropriate for a "new since you last looked" row stripe
   * that still leaves the row text readable. Falls back to a neutral
   * teal-tinted stripe when the customer has no accent color set.
   */
  newRowTint(slug: string | null | undefined, alpha = 0.12): string {
    const hex = this.accentFor(slug);
    if (!hex) return `rgba(45, 212, 191, ${alpha})`; // app-teal-100-ish fallback
    const rgb = this._hexToRgb(hex);
    if (!rgb) return `rgba(45, 212, 191, ${alpha})`;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  }

  /**
   * Returns the customer's accent color as a `R, G, B` comma-separated
   * triplet for use in a CSS custom property — the live-row-fresh
   * fade animation reads `rgba(var(--row-flash-color), 0.30)` etc, so
   * the pulse colors itself per customer. Falls back to a teal triplet
   * when the customer has no accent color set.
   */
  flashRgb(slug: string | null | undefined): string {
    const hex = this.accentFor(slug);
    if (!hex) return '20, 184, 166'; // teal-500 — matches legacy keyframe
    const rgb = this._hexToRgb(hex);
    if (!rgb) return '20, 184, 166';
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  }

  private _hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return null;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some(n => Number.isNaN(n))) return null;
    return { r, g, b };
  }
}
